import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import User from 'flarum/common/models/User';
import PostUser from 'flarum/forum/components/PostUser';
import listItems from 'flarum/common/helpers/listItems';
import UserCard from 'flarum/forum/components/UserCard';
import Button from 'flarum/common/components/Button';
import Modal from 'flarum/common/components/Modal';
import humanTime from 'flarum/common/utils/humanTime';
const STAFF = ['Founders', 'Developer', 'Surf Manager', 'GMOD Manager', 'Surf Admin', 'GMOD Admin'];

// Use Flarum's animation path, with a fallback when transitionend never fires.
function dismissProfileModal(modal) {
  const state = app.modal;
  const key = state.modal?.key;
  modal.hide();
  setTimeout(() => {
    if (state.modal && state.modal.key === key) {
      // Let the native close callback reset its animation state as well.
      document.querySelector(`.ModalManager[data-modal-key="${key}"] .Modal`)?.dispatchEvent(new Event('transitionend', {
        bubbles: true
      }));
    }
    if (state.modal && state.modal.key === key) state.close();
    if (!state.isModalOpen()) {
      state.backdropShown = false;
      m.redraw();
    }
  }, 400);
}
class BannerModal extends Modal {
  className() {
    return 'Modal--small HideoutBannerModal';
  }
  title() {
    return 'Change Banner';
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.file = null;
    this.busy = false;
    this.error = null;
  }
  content() {
    return m('div', {
      className: 'Modal-body'
    }, m('p', 'GIF, PNG, JPEG or JPG · maximum 10 MB · up to 2048 × 1024 pixels. GIFs: up to 120 frames and 50 million total pixels.'), m('label', {
      for: 'hideout-banner-file'
    }, 'Choose profile banner'), m('input', {
      id: 'hideout-banner-file',
      type: 'file',
      accept: '.gif,.png,.jpeg,.jpg',
      disabled: this.busy,
      onchange: event => {
        this.file = event.target.files[0];
        this.error = null;
      }
    }), false && m('p', {
      className: 'Alert Alert--error',
      role: 'alert'
    }, this.error), m(Button, {
      className: 'Button Button--primary',
      loading: this.busy,
      disabled: !this.file || this.busy,
      onclick: () => this.upload()
    }, 'Save Banner'));
  }
  async upload() {
    if (!this.file || this.busy) return;
    if (this.file.size > 10 * 1024 * 1024) {
      this.error = 'The banner must be at most 10 MB.';
      return;
    }
    this.busy = true;
    this.error = null;
    const body = new FormData();
    body.append('banner', this.file);
    try {
      const result = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/hideout/profile/banner',
        body
      });
      this.attrs.user.pushAttributes({
        hideoutBannerUrl: result.url
      });
      dismissProfileModal(this);
    } catch (error) {
      this.error = error?.response?.message || 'The banner could not be uploaded.';
    } finally {
      this.busy = false;
      m.redraw();
    }
  }
}
class BioModal extends Modal {
  className() {
    return 'Modal--small HideoutBioModal';
  }
  title() {
    return 'Edit bio';
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.bio = this.attrs.user.attribute('hideoutBio') || '';
    this.busy = false;
  }
  content() {
    return m('div.Modal-body', m('label', {
      for: 'hideout-bio'
    }, 'Tell the community about yourself'), m('textarea.FormControl', {
      id: 'hideout-bio',
      rows: 5,
      maxlength: 1000,
      value: this.bio,
      oninput: e => {
        this.bio = e.target.value;
      },
      disabled: this.busy
    }), m('p', 'Your bio is public. Maximum 1,000 characters.'), m(Button, {
      className: 'Button Button--primary',
      loading: this.busy,
      onclick: () => this.saveBio()
    }, 'Save bio'));
  }
  async saveBio() {
    if (this.busy) return;
    this.busy = true;
    try {
      await this.attrs.user.save({
        hideoutBio: this.bio.trim()
      });
      sessionStorage.removeItem('hideout:forum-draft:' + app.session.user.id() + ':' + location.pathname);
      dismissProfileModal(this);
      app.alerts.show({
        type: 'success'
      }, 'Bio saved.');
    } catch {
      app.alerts.show({
        type: 'error'
      }, 'Your bio could not be saved. Your text has been kept.');
    } finally {
      this.busy = false;
      m.redraw();
    }
  }
}
export default function profile() {
  // Replace canonical role badges in every existing Flarum badge list, including posts.
  extend(User.prototype, 'badges', function (items) {
    const groups = this.groups() || [];
    groups.forEach(group => {
      const name = group.nameSingular();
      if (STAFF.includes(name)) {
        items.remove('group' + group.id());
        items.add('hideout-staff-' + group.id(), m('span', {
          className: 'HideoutStaffRibbon',
          title: name
        }, name), 100 - STAFF.indexOf(name));
      } else if (['Member', 'Guest'].includes(name)) {
        items.remove('group' + group.id());
        items.add('hideout-neutral-' + group.id(), m('span', {
          className: 'HideoutStaffRibbon',
          title: name
        }, name), 0);
      } else if (['Supporter', 'Supporter Plus'].includes(name)) {
        items.remove('group' + group.id());
        items.add('hideout-supporter-' + group.id(), m('span', {
          className: 'HideoutSupporterRole',
          title: name
        }, name), 10);
      }
    });
  });
  extend(PostUser.prototype, 'userViewItems', function (items, user) {
    const badges = user.badges().toArray();
    const isRole = badge => ['HideoutStaffRibbon', 'HideoutSupporterRole'].includes(badge?.attrs?.className);
    const roles = badges.filter(isRole);
    if (roles.length) {
      items.setContent('postUser-badges', m('ul', {
        className: 'PostUser-badges badges badges--packed'
      }, listItems(badges.filter(badge => !isRole(badge)))));
      items.add('hideout-post-roles', m('div', {
        className: 'HideoutPostRoles'
      }, roles), 80);
    }
  });
  override(UserCard.prototype, 'view', function (original) {
    const vnode = original();
    if (String(this.attrs.className || '').includes('UserHero')) {
      const url = this.attrs.user.attribute('hideoutBannerUrl');
      if (url) vnode.attrs.style = {
        ...(vnode.attrs.style || {}),
        '--hideout-profile-banner': `linear-gradient(rgba(11,12,14,.55),rgba(11,12,14,.75)), url(${JSON.stringify(url)})`
      };
    }
    return vnode;
  });
  extend(UserCard.prototype, 'controlsItems', function (items) {
    if (String(this.attrs.className || '').includes('UserHero') && this.attrs.user.attribute('canUploadHideoutBanner')) {
      items.add('hideout-change-banner', m(Button, {
        className: 'Button HideoutChangeBanner',
        icon: 'far fa-image',
        onclick: () => app.modal.show(BannerModal, {
          user: this.attrs.user
        })
      }, 'Change Banner'), 110);
    }
  });
  extend(UserCard.prototype, 'infoItems', function (items) {
    const user = this.attrs.user;
    if (String(this.attrs.className || '').includes('UserHero')) {
      const own = app.session.user && app.session.user.id() === user.id();
      const bio = user.attribute('hideoutBio') || '';
      items.add('hideout-bio', m('section.HideoutProfileBio', {
        'aria-label': 'Bio'
      }, m('div.HideoutBioHeading', m('strong', 'Bio'), own && m(Button, {
        className: 'Button Button--link',
        onclick: () => app.modal.show(BioModal, {
          user
        })
      }, bio ? 'Edit bio' : 'Add bio')), m('p', bio || 'No bio added yet.')), -100);
    }
    const joined = user.joinTime();
    if (joined) items.setContent('joined', m('span', {
      title: joined.toLocaleString()
    }, 'Joined ', joined.toLocaleDateString()));
    const lastSeen = user.lastSeenAt();
    // Keep Flarum's disclosure preference: an omitted lastSeenAt stays omitted.
    if (lastSeen) items.setContent('lastSeen', m('span', {
      className: user.isOnline() ? 'online' : '',
      title: lastSeen.toLocaleString()
    }, user.isOnline() ? 'Online · Last seen just now' : ['Last seen ', humanTime(lastSeen)]));
  });
}